import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, getFirestore } from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaSave, FaTimes } from 'react-icons/fa';
import * as Yup from 'yup';
import { useFormik } from 'formik';

const HabilidadForm = ({ habilidad, onSave, onCancel, usuario }) => {
    // Estado para manejar categorías existentes
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(false);
    // Nuevo estado para controlar la creación de categoría
    const [creandoNuevaCategoria, setCreandoNuevaCategoria] = useState(false);
    
    // Acceder a Firestore
    const db = getFirestore(firebaseApp);
    
    // Obtener RUC y nombre de la empresa del usuario
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';
    const userEmail = usuario?.email || '';

    // Esquema de validación con Yup
    const validationSchema = Yup.object({
        nombre: Yup.string()
            .required('El nombre de la habilidad es obligatorio')
            .min(3, 'El nombre debe tener al menos 3 caracteres')
            .max(50, 'El nombre no puede exceder los 50 caracteres'),
        categoria: Yup.string()
            .nullable(),
        descripcion: Yup.string()
            .nullable()
            .max(200, 'La descripción no puede exceder los 200 caracteres')
    });

    // Inicializar formik
    const formik = useFormik({
        initialValues: {
            nombre: habilidad?.nombre || '',
            categoria: habilidad?.categoria || '',
            descripcion: habilidad?.descripcion || ''
        },
        validationSchema,
        onSubmit: handleSubmit
    });

    // Cargar categorías existentes (únicas) al iniciar
    useEffect(() => {
        loadCategorias();
    }, [ruc]);

    // Función para cargar categorías existentes
    const loadCategorias = async () => {
        try {
            setLoading(true);
            const habilidadesRef = collection(db, 'rs_habilidades');
            const q = query(
                habilidadesRef,
                where('ruc', '==', ruc),
                orderBy('categoria')
            );
            
            const snapshot = await getDocs(q);
            
            // Extraer categorías únicas
            const uniqueCategorias = [...new Set(
                snapshot.docs
                    .map(doc => doc.data().categoria)
                    .filter(Boolean) // Eliminar valores null/undefined
            )].sort();
            
            setCategorias(uniqueCategorias);
        } catch (error) {
            console.error("Error cargando categorías:", error);
        } finally {
            setLoading(false);
        }
    };

    // Manejar el cambio en el select de categoría
    const handleCategoriaChange = (e) => {
        const value = e.target.value;
        if (value === 'nueva') {
            setCreandoNuevaCategoria(true);
            formik.setFieldValue('categoria', ''); // Limpiar el valor para la nueva categoría
        } else {
            setCreandoNuevaCategoria(false);
            formik.setFieldValue('categoria', value);
        }
    };

    // Función para guardar la habilidad
    async function handleSubmit(values, { setSubmitting }) {
        try {
            setLoading(true);
            
            // Preparar objeto para guardar
            const habilidadData = {
                nombre: values.nombre.trim(),
                categoria: values.categoria ? values.categoria.trim() : null,
                descripcion: values.descripcion ? values.descripcion.trim() : '',
                ruc,
                empresa: nombreEmpresa
            };

            if (habilidad) {
                // Si es edición, usar los datos pasados al componente
                onSave({
                    ...habilidadData,
                    usos: habilidad.usos || 0,
                    fechaActualizacion: serverTimestamp(),
                    actualizadoPor: userEmail
                });
            } else {
                // Si es creación, crear nuevo documento
                const docRef = await addDoc(collection(db, 'rs_habilidades'), {
                    ...habilidadData,
                    usos: 0,
                    fechaCreacion: serverTimestamp(),
                    creadoPor: userEmail
                });
                
                onSave({
                    id: docRef.id,
                    ...habilidadData,
                    usos: 0
                });
            }
        } catch (error) {
            console.error("Error guardando habilidad:", error);
            throw error; // Reenviar el error para manejarlo en el componente padre
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    }

    return (
        <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
                <Form.Label>Nombre de la habilidad <span className="text-danger">*</span></Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Ej: Trabajo en equipo"
                    {...formik.getFieldProps('nombre')}
                    isInvalid={formik.touched.nombre && !!formik.errors.nombre}
                />
                <Form.Control.Feedback type="invalid">
                    {formik.errors.nombre}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                    El nombre debe ser claro y descriptivo
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Categoría</Form.Label>
                <Form.Select
                    value={creandoNuevaCategoria ? 'nueva' : formik.values.categoria}
                    onChange={handleCategoriaChange}
                >
                    <option value="">-- Sin categoría --</option>
                    {categorias.map((cat, index) => (
                        <option key={index} value={cat}>
                            {cat}
                        </option>
                    ))}
                    <option value="nueva">-- Crear nueva categoría --</option>
                </Form.Select>
                <Form.Text className="text-muted">
                    Agrupe habilidades similares en categorías
                </Form.Text>
            </Form.Group>

            {creandoNuevaCategoria && (
                <Form.Group className="mb-3">
                    <Form.Label>Nueva categoría</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Ej: Habilidades técnicas"
                        value={formik.values.categoria}
                        onChange={(e) => formik.setFieldValue('categoria', e.target.value)}
                    />
                </Form.Group>
            )}

            <Form.Group className="mb-3">
                <Form.Label>Descripción (opcional)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Describa brevemente esta habilidad..."
                    {...formik.getFieldProps('descripcion')}
                    isInvalid={formik.touched.descripcion && !!formik.errors.descripcion}
                />
                <Form.Control.Feedback type="invalid">
                    {formik.errors.descripcion}
                </Form.Control.Feedback>
            </Form.Group>

            {habilidad && (
                <div className="mb-3 text-muted">
                    <small>
                        Esta habilidad se está utilizando en {habilidad.usos || 0} vacantes
                    </small>
                </div>
            )}

            <Row className="mt-4">
                <Col className="d-flex justify-content-end gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        <FaTimes className="me-1" /> Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || formik.isSubmitting}
                    >
                        {loading || formik.isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-1"
                                />
                                {' '} Guardando...
                            </>
                        ) : (
                            <>
                                <FaSave className="me-1" /> Guardar
                            </>
                        )}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default HabilidadForm;